import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  DownloadIcon,
  EllipsisVerticalIcon,
  UploadIcon,
  XIcon,
} from 'lucide-react';
import { ANONYMOUS_PROJECT_TTL_MS, TIER_LABELS, TIER_LIMITS } from '@mockd/shared';
import type { Project, ProjectPermissions, CreateEndpointRequest, Tier } from '@mockd/shared';
import { useProjects, useEndpoints, useAuth, useUsage } from '../hooks';
import { EndpointList, EndpointForm, ImportEndpointModal, ExportEndpointModal } from '../components/endpoint';
import { ShareLinkPanel } from '../components/project/ShareLinkPanel';
import { ProjectVariablesPanel } from '../components/project/ProjectVariablesPanel';
import { TransferProjectModal } from '../components/project/TransferProjectModal';
import { RenameSubdomainModal } from '../components/project/RenameSubdomainModal';
import { Alert, Breadcrumbs, CopyButton, ConfirmDialog, LimitIndicator, Modal, ReadOnlyBadge, READ_ONLY_HINT } from '../components/common';
import { Skeleton } from '../components/Skeleton';
import { getMockApiSubdomainUrl } from '../config';
import { ROUTES } from '../routes';
import type { EndpointImportItem } from '../utils/exportEndpoint';

export function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { getProject, updateProject, deleteProject, claimProject } = useProjects();
  const { endpoints, loading: endpointsLoading, fetchEndpoints, createEndpoint, importEndpoints } = useEndpoints();
  const { usage } = useUsage(projectId);

  const [project, setProject] = useState<Project | null>(null);
  // Held apart from `project` because rename/claim/transfer responses carry no
  // permissions or tier. A rename keeps both; claim and transfer re-fetch them.
  const [permissions, setPermissions] = useState<ProjectPermissions | null>(null);
  // The tier that gates this project — the team owner's on a team project —
  // not the viewer's own (#260).
  const [effectiveTier, setEffectiveTier] = useState<Tier>('free');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [showDeleteProject, setShowDeleteProject] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [showGettingStarted, setShowGettingStarted] = useState(searchParams.get('from') === 'template');
  const [showImport, setShowImport] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showShareLink, setShowShareLink] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showRenameSubdomain, setShowRenameSubdomain] = useState(false);

  const isAnonymous = project !== null && !project.userId;
  const canClaim = isAnonymous && !!user;
  // Unclaimed projects are deleted, mocks and logs included, once this passes.
  const expiresOn = isAnonymous
    ? new Date(new Date(project.createdAt).getTime() + ANONYMOUS_PROJECT_TTL_MS).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  // Claim and transfer move the project to a new owner, which changes both the
  // caller's permissions and the tier that gates it, so re-read them.
  const reloadProject = async () => {
    if (!projectId) return;
    const data = await getProject(projectId);
    setProject(data);
    setPermissions(data.permissions);
    setEffectiveTier(data.effectiveTier);
  };

  const handleTransferred = async (transferred: Project) => {
    setProject(transferred);
    try {
      await reloadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project');
    }
  };

  const handleClaimProject = async () => {
    if (!projectId) return;
    setIsClaiming(true);
    try {
      setProject(await claimProject(projectId));
      await reloadProject();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to claim project');
    } finally {
      setIsClaiming(false);
    }
  };

  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [projectData] = await Promise.all([
          getProject(projectId),
          fetchEndpoints(projectId),
        ]);
        setProject(projectData);
        setPermissions(projectData.permissions);
        setEffectiveTier(projectData.effectiveTier);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, getProject, fetchEndpoints]);

  const handleCreateEndpoint = async (data: CreateEndpointRequest) => {
    if (!projectId) return;

    setIsCreating(true);
    try {
      await createEndpoint(projectId, data);
      setShowForm(false);
    } finally {
      setIsCreating(false);
    }
  };

  const handleImportEndpoints = async (items: EndpointImportItem[]) => {
    if (!projectId) return;
    setIsImporting(true);
    try {
      await importEndpoints(projectId, items);
    } finally {
      setIsImporting(false);
    }
  };

  const handleEditProject = () => {
    if (!project) return;
    setEditName(project.name);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!projectId || !editName.trim()) return;

    try {
      const updated = await updateProject(projectId, { name: editName.trim() });
      setProject(updated);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditName('');
  };

  const handleDeleteProject = () => {
    setShowDeleteProject(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectId) return;
    await deleteProject(projectId);
    navigate('/');
  };

  if (loading) {
    return <Skeleton variant="project-detail" />;
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-error mb-4">{error || 'Project not found'}</h1>
          <Link to="/" className="link link-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  const endpointBaseUrl = getMockApiSubdomainUrl(project.subdomain);
  const endpointLimit = TIER_LIMITS[effectiveTier].endpointsPerProject;
  const atEndpointLimit = endpoints.length >= endpointLimit;
  const retentionDays = TIER_LIMITS[effectiveTier].logRetentionDays;
  // Business is the top plan, so there's nothing to offer an upgrade to.
  const canUpgrade = effectiveTier !== 'business';
  // Gate on the server's resolved permissions, never on `project.userId`: on a
  // team project that is only the creator, while owners/admins manage it too (#261).
  const canWrite = permissions?.canWrite ?? false;
  const canManage = permissions?.canManageProject ?? false;
  // Variables, share links, transfer and subdomain changes all need a signed-in
  // owner, so they are never offered on anonymous projects (no userId). The
  // backend enforces the finer transfer rules (e.g. only owner/admin may move a
  // team project out).
  const canEditVariables = canWrite && !!project.userId;
  const canShare = canManage && !!project.userId;
  const canTransfer = canManage && !!project.userId;
  const canRenameSubdomain = canManage && !!project.userId;

  return (
    <div className="min-h-screen bg-base-200">
      <header className="bg-base-100 border-b border-base-300">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <Breadcrumbs
            items={[
              { label: 'Home', href: '/dashboard' },
              { label: project.name },
            ]}
          />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-4">
              <div>
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    {/* The page keeps its title while the name is being edited. */}
                    <h1 className="sr-only">{project.name}</h1>
                    <input
                      type="text"
                      aria-label="Project name"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="input input-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit();
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                    />
                    <button onClick={handleSaveEdit} className="btn btn-primary btn-sm">Save</button>
                    <button onClick={handleCancelEdit} className="btn btn-ghost btn-sm">Cancel</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h1 className="text-base-content">{project.name}</h1>
                    {!canWrite && <ReadOnlyBadge />}
                  </div>
                )}
                <p className="text-sm text-base-content/80">
                  <code className="text-xs bg-base-200 px-1.5 py-0.5 rounded-field">{project.subdomain}</code>
                </p>
              </div>
            </div>
            {/* canManage implies canWrite, so a read-only caller gets no menu at all. */}
            {canWrite && (
              <div className="dropdown dropdown-end">
                <label tabIndex={0} className="btn btn-ghost btn-sm btn-square" aria-label="Project settings">
                  <EllipsisVerticalIcon className="size-5" />
                </label>
                <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 bg-base-100 border border-base-300 rounded-box w-52">
                  <li>
                    <button onClick={() => { (document.activeElement as HTMLElement)?.blur(); handleEditProject(); }}>
                      Edit Project
                    </button>
                  </li>
                  {canRenameSubdomain && (
                    <li>
                      <button onClick={() => { (document.activeElement as HTMLElement)?.blur(); setShowRenameSubdomain(true); }}>
                        Change Subdomain
                      </button>
                    </li>
                  )}
                  {canShare && (
                    <li>
                      <button onClick={() => { (document.activeElement as HTMLElement)?.blur(); setShowShareLink(true); }}>
                        Share Project
                      </button>
                    </li>
                  )}
                  {canTransfer && (
                    <li>
                      <button onClick={() => { (document.activeElement as HTMLElement)?.blur(); setShowTransfer(true); }}>
                        Transfer Project
                      </button>
                    </li>
                  )}
                  {canManage && (
                    <li>
                      <button onClick={() => { (document.activeElement as HTMLElement)?.blur(); handleDeleteProject(); }} className="text-error">
                        Delete Project
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="card bg-base-100 mb-6">
          <div className="px-4 py-3 border-b border-base-200">
            <h2 className="text-base-content">Access</h2>
          </div>
          <div className="p-4">
            <div>
              <label className="text-sm text-base-content/80 block mb-1">Mock API URL</label>
              <span className="inline-flex items-center gap-1">
                <code className="text-sm font-mono text-base-content break-all">{endpointBaseUrl}</code>
                <CopyButton text={endpointBaseUrl} iconOnly />
              </span>
            </div>
          </div>
        </div>
        {canEditVariables && <ProjectVariablesPanel projectId={projectId!} />}

        {showGettingStarted && endpoints.length > 0 && (
          <Alert
            variant="success"
            className="mb-6"
            title="Your mock API is ready. Try it out:"
            action={
              <>
                <CopyButton
                  text={`curl ${endpointBaseUrl}${endpoints[0].path}`}
                  label="Copy"
                  className="btn-sm"
                />
                <button
                  onClick={() => {
                    setShowGettingStarted(false);
                    setSearchParams({});
                  }}
                  className="btn btn-ghost btn-sm btn-square"
                  aria-label="Dismiss getting started"
                >
                  <XIcon className="size-4" />
                </button>
              </>
            }
          >
            <code className="font-mono block truncate">
              curl {endpointBaseUrl}{endpoints[0].path}
            </code>
          </Alert>
        )}

        {isAnonymous && (
          <Alert
            variant="info"
            className="mb-6"
            title="This is a temporary project"
            action={canClaim ? (
              <button
                onClick={handleClaimProject}
                disabled={isClaiming}
                className="btn btn-primary btn-sm"
              >
                {isClaiming ? 'Claiming...' : 'Claim Project'}
              </button>
            ) : (
              <Link
                to={`${ROUTES.auth.register()}?redirect=${encodeURIComponent(`/projects/${project.id}`)}`}
                className="btn btn-primary btn-sm"
              >
                Sign up to keep it
              </Link>
            )}
          >
            It will be deleted on {expiresOn}, along with its endpoints and request logs, and only this browser can open it.{' '}
            {canClaim
              ? 'Claim it to save it to your account permanently.'
              : 'Sign up free to keep it.'}
          </Alert>
        )}

        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-base-content">Endpoints</h2>
              <span className={`text-xs ${endpoints.length >= endpointLimit ? 'text-error font-medium' : 'text-base-content/70'}`}>
                {endpoints.length}/{endpointLimit}
              </span>
              {usage && (
                // The counter keeps climbing on rejected requests; cap it so the bar reads "full", not "over".
                <div className="w-48">
                  <LimitIndicator
                    used={Math.min(usage.count, usage.limit)}
                    max={usage.limit}
                    label="Daily requests"
                    upgradeLink={canUpgrade ? undefined : null}
                  />
                </div>
              )}
              <span className="text-xs text-base-content/70">
                {retentionDays}-day log retention
              </span>
            </div>
            {!showForm && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowForm(true)}
                  disabled={!canWrite || atEndpointLimit}
                  className="btn btn-primary"
                  title={!canWrite
                    ? READ_ONLY_HINT
                    : atEndpointLimit ? `Endpoint limit reached${canUpgrade ? ' — upgrade to add more' : ''}` : undefined}
                >
                  Add Endpoint
                </button>
                <div className="dropdown dropdown-end">
                  <label tabIndex={0} className="btn btn-ghost btn-sm btn-square" aria-label="Endpoint actions">
                    <EllipsisVerticalIcon className="size-5" />
                  </label>
                  <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 bg-base-100 border border-base-300 rounded-box w-52">
                    <li>
                      <button
                        onClick={() => { (document.activeElement as HTMLElement)?.blur(); setShowImport(true); }}
                        disabled={!canWrite}
                        title={canWrite ? undefined : READ_ONLY_HINT}
                      >
                        <UploadIcon className="size-4" />
                        Import
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => { (document.activeElement as HTMLElement)?.blur(); setShowExport(true); }}
                        disabled={endpoints.length === 0}
                        title={endpoints.length === 0 ? "No endpoints to export" : "Export endpoints as cURL, JSON, or OpenAPI"}
                      >
                        <DownloadIcon className="size-4" />
                        Export
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          {atEndpointLimit && (
            <Alert
              variant="warning"
              className="mt-3 py-2"
              action={canUpgrade && <Link to={ROUTES.pricing()} className="btn btn-sm btn-primary">Upgrade</Link>}
            >
              You've reached the {endpointLimit}-endpoint limit on the {TIER_LABELS[effectiveTier]} plan.
            </Alert>
          )}
        </div>

        {showForm && (
          <div className="card bg-base-100 mb-6 p-6">
            <h2 className="text-base-content mb-4">Create New Endpoint</h2>
            <EndpointForm
              onSubmit={handleCreateEndpoint}
              onCancel={() => setShowForm(false)}
              isLoading={isCreating}
            />
          </div>
        )}

        {endpointsLoading && endpoints.length === 0 ? (
          <Skeleton variant="endpoint-list" />
        ) : (
          <EndpointList
            projectId={projectId!}
            endpoints={endpoints}
            emptyAction={canWrite && !atEndpointLimit && !showForm && (
              <button onClick={() => setShowForm(true)} className="btn btn-primary btn-sm">
                Create endpoint
              </button>
            )}
          />
        )}
      </main>

      <ConfirmDialog
        isOpen={showDeleteProject}
        title="Delete Project"
        message={`Are you sure you want to delete "${project.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={confirmDeleteProject}
        onCancel={() => setShowDeleteProject(false)}
      />

      <ImportEndpointModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImport={handleImportEndpoints}
        isLoading={isImporting}
      />

      <ExportEndpointModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        projectId={projectId!}
        projectName={project.name}
        baseUrl={endpointBaseUrl}
        endpoints={endpoints}
      />

      <Modal
        isOpen={showShareLink}
        onClose={() => setShowShareLink(false)}
        title="Share Project"
        titleClassName="font-bold text-lg mb-4"
      >
        <ShareLinkPanel projectId={projectId!} />
        <div className="modal-action">
          <button type="button" className="btn" onClick={() => setShowShareLink(false)}>
            Close
          </button>
        </div>
      </Modal>

      <TransferProjectModal
        project={project}
        isOpen={showTransfer}
        onClose={() => setShowTransfer(false)}
        onTransferred={handleTransferred}
      />

      <RenameSubdomainModal
        project={project}
        isOpen={showRenameSubdomain}
        onClose={() => setShowRenameSubdomain(false)}
        onRenamed={setProject}
      />
    </div>
  );
}
